import React from "react";
import { Badge, Card, Portal, Text } from "@fluentui/react-components";
import SourceIcon from "@components/SourceIcon/SourceIcon";
import { useConfig } from "@contexts/ConfigContext";
import type { TourDemoCardPlacement } from "./BoardTour.ViewModel.types";

const DEMO_SOURCE_TYPE_ID = "github";

export interface BoardTourDemoCardProps {
  placement: TourDemoCardPlacement;
  title: string;
  badgeText: string;
}

/**
 * A stand-in task card for the tour's card and source-icon steps on a board
 * that has none. It borrows the real card's classes so it reads as one, carries
 * the same data-tour anchors, and is inert: nothing on it can be clicked or
 * dragged.
 *
 * Fluent's Portal rather than the tour's own: that one mounts on the body,
 * outside the FluentProvider, and the card came out with none of the theme.
 */
const BoardTourDemoCard: React.FC<BoardTourDemoCardProps> = ({
  placement,
  title,
  badgeText,
}) => {
  const { config } = useConfig();

  return (
    <Portal>
      <Card
        appearance="outline"
        size="small"
        className="board-view__task-card board-tour__demo-card"
        data-tour="task-card"
        aria-hidden="true"
        // Inline, not in BoardTour.scss: Fluent's own position class would win.
        style={{
          position: "fixed",
          top: placement.top,
          left: placement.left,
          width: placement.width,
        }}
      >
        <div className="board-view__task">
          <div className="board-view__task-header-row">
            <Text className="board-view__task-title">{title}</Text>
          </div>
        </div>
        <div className="board-view__task-footer-row">
          <Badge appearance="tint" size="small">
            {badgeText}
          </Badge>
          <div className="board-view__task-source-links">
            <span
              className="board-view__task-source-link-drag"
              data-tour="task-source-icon"
            >
              <span className="board-view__task-source-link">
                <SourceIcon
                  src={`${config?.regionBaseUrl ?? ""}/assets/img/sourceicons/${DEMO_SOURCE_TYPE_ID}.png`}
                />
              </span>
            </span>
          </div>
        </div>
      </Card>
    </Portal>
  );
};

export default BoardTourDemoCard;
